import { useApolloClient } from '@apollo/client';
import IconButton from '@mui/material/IconButton';
import { PickerFileMetadata } from 'filestack-js';
import { useCallback } from 'react';
import { ConnectedProps, connect } from 'react-redux';

import { popup } from '@/actions/ui';
import { openFileDialog } from '@/core/attachment';
import { useFileUploadErrorHandler } from '@/hooks/useFileUploadErrorHandler';

import Button from '../Button';
import FAIcon from '../Icon/FAIcon';

interface CvButtonProps {
  url?: string;
  label?: string;
  editable?: boolean;
  onUpload: (file: PickerFileMetadata) => void;
  onRemove?: any;
}

const connector = connect(undefined, {
  popup,
});

const CvButton = ({
  url,
  label = 'Attach CV',
  editable = true,
  popup,
  onRemove,
  onUpload,
}: CvButtonProps & ConnectedProps<typeof connector>) => {
  const graphql = useApolloClient();
  const onUploadError = useFileUploadErrorHandler();

  const upload = useCallback(async () => {
    const files = await openFileDialog(graphql, {
      accept: ['.doc', '.docx', '.pdf', '.rtf'],
      maxSize: 3 * 1024 * 1024, // 3 MB
      fromSources: [
        'local_file_system',
        'url',
        'googledrive',
        'dropbox',
        'box',
        'onedrive',
        'onedriveforbusiness',
      ],
    }).catch((error) => onUploadError(error, 'CV'));
    if (files?.length) onUpload(files[0]);
  }, [graphql, onUpload, onUploadError]);

  const remove = useCallback(() => {
    popup({
      buttonAlignment: 'space-between',
      title: 'Do you want to remove the attached CV from the profile?',
      buttons: [
        { flat: true, label: 'Cancel' },
        {
          label: 'Remove',
          callback: onRemove,
        },
      ],
    });
  }, [onRemove, popup]);

  if (url) {
    return (
      <>
        <Button href={url} size="medium">
          Open CV
        </Button>
        {editable && (
          <IconButton onClick={remove} size="small" style={{ marginLeft: 10 }}>
            <FAIcon icon="trash" />
          </IconButton>
        )}
      </>
    );
  }

  if (!editable) return null;

  return (
    <Button size="medium" onClick={upload}>
      {label}
    </Button>
  );
};

export default connector(CvButton);
